/**
 * Regional Calculator Helpers
 *
 * Region-aware calculation helpers for cost calculator.
 * Supports EU (metric) and UK/US (imperial) units.
 *
 * Related Spec: uk-us-expansion
 * Task: Task 12 - Refactor useVehicleCostCalculator hook for regional formulas
 */

import logger from '../utils/logger.js';
import { FUEL_PRICE_CONFIG } from '../utils/formatters.js';

/**
 * Configuration constants for vehicle cost calculator
 */
export const CONFIG = {
  SCALING_CAPS: {
    min: 0.5,
    max: 2.0,
  },
  DEFAULT_VALUES: {
    fuelPrice: 1.5, // Fallback for EU
    monthlyDistance: 1250, // Fallback for EU (km)
    annualDepreciation: 10.0,
    monthlyOwnershipCost: 150,
  },
  ROUNDING_DECIMALS: 2,
};

/**
 * Distance range mappings from onboarding answers (in kilometers, normalized)
 * These are abstract tier IDs that map to different display values per region
 */
export const DISTANCE_RANGES = {
  // EU tiers (5 tiers) - in kilometers
  distance_tier_1: { min: 0, max: 5000 }, // 0 - 5,000 km/year
  distance_tier_2: { min: 5000, max: 10000 }, // 5,000 - 10,000 km/year
  distance_tier_3: { min: 10000, max: 15000 }, // 10,000 - 15,000 km/year
  distance_tier_4: { min: 15000, max: 20000 }, // 15,000 - 20,000 km/year
  distance_tier_5: { min: 20000, max: 25000 }, // 20,000+ km/year

  // Legacy support for old km-based IDs (will be phased out)
  distance_km_under_5k: { min: 0, max: 5000 },
  distance_km_5k_10k: { min: 5000, max: 10000 },
  distance_km_10k_20k: { min: 10000, max: 20000 },
  distance_km_20k_30k: { min: 20000, max: 30000 },
  distance_km_over_30k: { min: 30000, max: 35000 },
};

/**
 * Unit conversion constants
 */
export const CONVERSIONS = {
  KM_TO_MILES: 0.621371,
  MILES_TO_KM: 1.60934,
  L_PER_100KM_TO_MPG_UK: 282.481, // UK gallon (4.54609 L)
  L_PER_100KM_TO_MPG_US: 235.215, // US gallon (3.78541 L)
  MPG_UK_TO_L_PER_100KM: 282.481,
  MPG_US_TO_L_PER_100KM: 235.215,
};

/**
 * Regional fuel consumption field name mapping
 * Maps region to the field name used in static_primary_specs.engine_options
 *
 * Related Spec: uk-us-expansion
 * Task: Task 12a - Regional field name extraction
 */
export const FUEL_CONSUMPTION_FIELD_MAP = {
  EU: 'fuel_consumption_l_100km_combined',
  UK: 'fuel_consumption_mpg_imperial_combined',
  US: 'fuel_consumption_mpg_us_combined',
};

/**
 * Regional acceleration field name mapping
 */
export const ACCELERATION_FIELD_MAP = {
  EU: 'acceleration_0_100_kmh_sec',
  UK: 'acceleration_0_60_mph_sec',
  US: 'acceleration_0_60_mph_sec',
};

/**
 * Default fuel consumption values per region (in native units)
 */
export const DEFAULT_FUEL_CONSUMPTION = {
  EU: 6.0, // L/100km
  UK: 45, // MPG Imperial
  US: 30, // MPG US
};

/**
 * Get the fuel consumption field name for a region
 *
 * @param {'EU'|'UK'|'US'} region - The region
 * @returns {string} Field name for fuel consumption in static_primary_specs
 */
export const getFuelConsumptionField = (region = 'EU') => {
  return FUEL_CONSUMPTION_FIELD_MAP[region] || FUEL_CONSUMPTION_FIELD_MAP.EU;
};

/**
 * Get the acceleration field name for a region
 *
 * @param {'EU'|'UK'|'US'} region - The region
 * @returns {string} Field name for acceleration in static_primary_specs
 */
export const getAccelerationField = (region = 'EU') => {
  return ACCELERATION_FIELD_MAP[region] || ACCELERATION_FIELD_MAP.EU;
};

/**
 * Extract fuel consumption from engine options based on region
 * Returns the native value in regional units
 *
 * @param {Array<Object>} engineOptions - Array of engine option objects from static_primary_specs
 * @param {'EU'|'UK'|'US'} region - The region
 * @returns {{value: number, unit: string, fieldName: string}} Fuel consumption with metadata
 */
export const extractFuelConsumption = (engineOptions, region = 'EU') => {
  const fieldName = getFuelConsumptionField(region);
  const defaultValue = DEFAULT_FUEL_CONSUMPTION[region] || DEFAULT_FUEL_CONSUMPTION.EU;

  if (!Array.isArray(engineOptions) || engineOptions.length === 0) {
    return {
      value: defaultValue,
      unit: region === 'EU' ? 'l_100km' : 'mpg',
      fieldName,
      isDefault: true,
    };
  }

  const engineOption = engineOptions[0];
  const value = engineOption?.[fieldName];

  if (typeof value === 'number' && value > 0) {
    return {
      value,
      unit: region === 'EU' ? 'l_100km' : 'mpg',
      fieldName,
      isDefault: false,
    };
  }

  // Fallback: try EU field if regional field not found (backwards compatibility)
  const euValue = engineOption?.fuel_consumption_l_100km_combined;
  if (typeof euValue === 'number' && euValue > 0) {
    return {
      value: euValue,
      unit: 'l_100km',
      fieldName: 'fuel_consumption_l_100km_combined',
      isDefault: false,
      fallbackUsed: true,
    };
  }

  return {
    value: defaultValue,
    unit: region === 'EU' ? 'l_100km' : 'mpg',
    fieldName,
    isDefault: true,
  };
};

/**
 * Convert fuel consumption to L/100km (normalized metric)
 * Used for calculator compatibility
 *
 * @param {number} value - Fuel consumption value
 * @param {'EU'|'UK'|'US'} region - Source region (determines unit type)
 * @returns {number} Fuel consumption in L/100km
 */
export const normalizeFuelConsumptionToLPer100km = (value, region = 'EU') => {
  if (!value || value <= 0) return 0;

  switch (region) {
    case 'EU':
      // Already in L/100km
      return value;
    case 'UK':
      // Convert Imperial MPG to L/100km
      return CONVERSIONS.MPG_UK_TO_L_PER_100KM / value;
    case 'US':
      // Convert US MPG to L/100km
      return CONVERSIONS.MPG_US_TO_L_PER_100KM / value;
    default:
      return value;
  }
};

/**
 * Sanitize and validate fuel consumption value for a region
 *
 * @param {number} value - Raw fuel consumption value
 * @param {'EU'|'UK'|'US'} region - The region
 * @returns {{value: number, isValid: boolean}} Sanitized value with validation status
 */
export const sanitizeFuelConsumption = (value, region = 'EU') => {
  const defaultValue = DEFAULT_FUEL_CONSUMPTION[region] || DEFAULT_FUEL_CONSUMPTION.EU;

  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return { value: defaultValue, isValid: false };
  }

  // Different valid ranges for L/100km vs MPG
  if (region === 'EU') {
    // L/100km: typically 3-20 for cars
    if (value < 1 || value > 30) {
      return { value: defaultValue, isValid: false };
    }
  } else {
    // MPG: typically 15-70 for cars
    if (value < 5 || value > 100) {
      return { value: defaultValue, isValid: false };
    }
  }

  return { value, isValid: true };
};

/**
 * Parse a range string like "€22,000 - €29,500" or "€100 - €160"
 * Also handles single values like "$45" or "€100"
 * Returns an object with min, max, and average values
 *
 * @param {string} rangeString - The range string to parse
 * @param {number} defaultValue - Fallback value if parsing fails
 * @returns {{min: number, max: number, average: number}}
 */
export const parseRangeString = (rangeString, defaultValue = 0) => {
  try {
    if (!rangeString || typeof rangeString !== 'string') {
      return { min: defaultValue, max: defaultValue, average: defaultValue };
    }

    // Remove currency symbols, letters (except for ranges like "per month"), and extra whitespace
    // Keep numbers, commas, periods, hyphens, and spaces
    const cleaned = rangeString.replace(/[€$£¥]/g, '').trim();

    // Split by dash or hyphen
    const parts = cleaned.split(/\s*[-–—]\s*/);

    if (parts.length === 2) {
      // Range format: "100 - 200"
      const min = parseFloat(parts[0].replace(/,/g, ''));
      const max = parseFloat(parts[1].replace(/,/g, ''));

      // Validate parsed values
      if (Number.isNaN(min) || Number.isNaN(max) || min < 0 || max < 0 || min > max) {
        return { min: defaultValue, max: defaultValue, average: defaultValue };
      }

      const average = (min + max) / 2;
      return { min, max, average };
    } else if (parts.length === 1) {
      // Single value format: "$45" or "100"
      const singleValue = parseFloat(parts[0].replace(/,/g, ''));

      if (!Number.isNaN(singleValue) && singleValue >= 0) {
        return { min: singleValue, max: singleValue, average: singleValue };
      }
    }

    // Fallback to default if parsing fails
    return { min: defaultValue, max: defaultValue, average: defaultValue };
  } catch (error) {
    logger.warn('Error parsing range string:', rangeString, error);
    return { min: defaultValue, max: defaultValue, average: defaultValue };
  }
};

/**
 * Convert onboarding distance answer to annual kilometer midpoint
 *
 * @param {string} distanceValue - The distance range identifier (e.g., "distance_km_20k_30k")
 * @param {number} defaultValue - Fallback value if unknown range
 * @returns {number} Annual distance in kilometers
 */
export const getDistanceMidpoint = (distanceValue, defaultValue = 15000) => {
  try {
    if (!distanceValue || typeof distanceValue !== 'string') {
      return defaultValue;
    }

    const range = DISTANCE_RANGES[distanceValue];

    if (!range) {
      logger.warn(`Unknown distance range: ${distanceValue}, using default ${defaultValue} km`);
      return defaultValue;
    }

    return (range.min + range.max) / 2;
  } catch (error) {
    logger.warn('Error getting distance midpoint:', distanceValue, error);
    return defaultValue;
  }
};

/**
 * Clamp a scaling factor between min and max bounds
 *
 * @param {number} scalingFactor - The raw scaling factor
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {number} Clamped scaling factor
 */
export const clampScalingFactor = (
  scalingFactor,
  min = CONFIG.SCALING_CAPS.min,
  max = CONFIG.SCALING_CAPS.max
) => {
  if (Number.isNaN(scalingFactor) || !Number.isFinite(scalingFactor)) {
    return 1.0; // Default to no scaling
  }
  return Math.max(min, Math.min(max, scalingFactor));
};

/**
 * Convert fuel consumption from L/100km to MPG (UK or US)
 *
 * @param {number} lPer100km - Fuel consumption in L/100km
 * @param {'UK'|'US'} region - Target region (UK or US)
 * @returns {number} Fuel consumption in MPG
 */
export const convertLPer100kmToMPG = (lPer100km, region) => {
  if (lPer100km <= 0) return 0;
  const conversionFactor =
    region === 'UK' ? CONVERSIONS.L_PER_100KM_TO_MPG_UK : CONVERSIONS.L_PER_100KM_TO_MPG_US;
  return conversionFactor / lPer100km;
};

/**
 * Convert fuel consumption from MPG to L/100km
 *
 * @param {number} mpg - Fuel consumption in MPG
 * @param {'UK'|'US'} region - Source region (UK or US)
 * @returns {number} Fuel consumption in L/100km
 */
export const convertMPGToLPer100km = (mpg, region) => {
  if (mpg <= 0) return 0;
  const conversionFactor =
    region === 'UK' ? CONVERSIONS.MPG_UK_TO_L_PER_100KM : CONVERSIONS.MPG_US_TO_L_PER_100KM;
  return conversionFactor / mpg;
};

/**
 * Convert distance from km to miles
 *
 * @param {number} km - Distance in kilometers
 * @returns {number} Distance in miles
 */
export const convertKmToMiles = (km) => {
  return km * CONVERSIONS.KM_TO_MILES;
};

/**
 * Convert distance from miles to km
 *
 * @param {number} miles - Distance in miles
 * @returns {number} Distance in kilometers
 */
export const convertMilesToKm = (miles) => {
  return miles * CONVERSIONS.MILES_TO_KM;
};

/**
 * Get default fuel price for a region
 *
 * @param {'EU'|'UK'|'US'} region - The region
 * @returns {number} Default fuel price per liter (or gallon for US)
 */
export const getDefaultFuelPrice = (region = 'EU') => {
  return FUEL_PRICE_CONFIG[region]?.default || CONFIG.DEFAULT_VALUES.fuelPrice;
};

/**
 * Validate and sanitize calculator inputs
 *
 * @param {Object} inputs - Raw input object
 * @param {'EU'|'UK'|'US'} region - User's region
 * @returns {{isValid: boolean, sanitized: Object, warnings: string[]}}
 */
export const validateCostInputs = (inputs, region = 'EU') => {
  const warnings = [];
  const sanitized = { ...inputs };

  // Validate estimated_price_range
  if (!inputs.estimated_price_range) {
    warnings.push('Missing estimated_price_range, using default');
    sanitized.estimated_price_range = '€20,000 - €30,000';
  }

  // Validate estimated_annual_depreciation_percentage
  if (
    typeof inputs.estimated_annual_depreciation_percentage !== 'number' ||
    inputs.estimated_annual_depreciation_percentage < 0 ||
    inputs.estimated_annual_depreciation_percentage > 100
  ) {
    warnings.push('Invalid depreciation percentage, using default');
    sanitized.estimated_annual_depreciation_percentage = CONFIG.DEFAULT_VALUES.annualDepreciation;
  }

  // Validate fuel_consumption_l_100km_combined
  if (
    typeof inputs.fuel_consumption_l_100km_combined !== 'number' ||
    inputs.fuel_consumption_l_100km_combined <= 0
  ) {
    warnings.push('Invalid fuel consumption, using default 6.0 L/100km');
    sanitized.fuel_consumption_l_100km_combined = 6.0;
  }

  // Validate monthly_ownership_cost
  if (!inputs.monthly_ownership_cost) {
    warnings.push('Missing monthly_ownership_cost, using default');
    // Use regional default value (numeric, parseRangeString will handle it)
    const currencySymbol = region === 'US' ? '$' : region === 'UK' ? '£' : '€';
    sanitized.monthly_ownership_cost = `${currencySymbol}100 - ${currencySymbol}200`;
  }

  // Validate onboarding_annual_distance_value
  if (!inputs.onboarding_annual_distance_value) {
    warnings.push('Missing onboarding distance, using default');
    sanitized.onboarding_annual_distance_value = 'distance_tier_3'; // Updated to abstract tier ID
  }

  // Validate userInput_monthly_distance_km
  if (
    typeof inputs.userInput_monthly_distance_km !== 'number' ||
    inputs.userInput_monthly_distance_km < 0
  ) {
    warnings.push('Invalid monthly distance input, using default');
    sanitized.userInput_monthly_distance_km = CONFIG.DEFAULT_VALUES.monthlyDistance;
  }

  // Validate userInput_fuel_price_per_liter (or per gallon for US)
  if (
    typeof inputs.userInput_fuel_price_per_liter !== 'number' ||
    inputs.userInput_fuel_price_per_liter <= 0
  ) {
    warnings.push('Invalid fuel price input, using regional default');
    sanitized.userInput_fuel_price_per_liter = getDefaultFuelPrice(region);
  }

  return {
    isValid: warnings.length === 0,
    sanitized,
    warnings,
  };
};

/**
 * Round a number to specified decimal places
 *
 * @param {number} value - The value to round
 * @param {number} decimals - Number of decimal places
 * @returns {number} Rounded value
 */
export const roundToDecimals = (value, decimals = CONFIG.ROUNDING_DECIMALS) => {
  if (Number.isNaN(value) || !Number.isFinite(value)) {
    return 0;
  }
  return Math.round(value * 10 ** decimals) / 10 ** decimals;
};
